import { produce } from "immer";
import {
  FETCH_PERMISSIONS_OVERVIEW,
  FETCH_PERMISSIONS_OVERVIEW_SUCCESS,
  FETCH_PERMISSIONS_OVERVIEW_FAILURE,
  FETCH_PERMISSION_HISTORY,
  FETCH_PERMISSION_HISTORY_SUCCESS,
  FETCH_PERMISSION_HISTORY_FAILURE,
} from "./constants";

export const initialState = {
  isFetching: false,
  // `null` = never loaded (distinct from `[]` = loaded and genuinely empty, which
  // is what the screen's empty state is for).
  employees: null,
  locations: [],
  // The integer location id the rows belong to — null means the whole company.
  locationId: null,
  // The endpoint answers 403 when the viewer may not manage permissions. That is a
  // legitimate answer, not a failure, so the screen renders NoPermission rather
  // than an error toast.
  forbidden: false,
  error: false,
  fetchedAt: null,

  // ----- one employee's permission history, for the open drawer -----
  isFetchingHistory: false,
  // The employee the entries below belong to. Held so the drawer never renders the
  // previous person's history against the current person's name.
  historyForUuid: null,
  // `null` = not loaded for this person yet; `[]` = loaded and genuinely empty.
  historyEntries: null,
  // The audit table's migration has not run — say "nothing recorded yet", never "error".
  historyAvailable: true,
};

const EmployeePermissionsReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCH_PERMISSIONS_OVERVIEW: {
        const nextLocation = action.payload?.locationId ?? null;
        draft.isFetching = true;
        draft.error = false;
        // Switching scope must not leave the previous scope's people on screen.
        if (nextLocation !== state.locationId) {
          draft.employees = null;
        }
        draft.locationId = nextLocation;
        break;
      }

      case FETCH_PERMISSIONS_OVERVIEW_SUCCESS:
        draft.isFetching = false;
        draft.error = false;
        draft.forbidden = false;
        draft.employees = action.payload?.employees ?? [];
        draft.locations = action.payload?.locations ?? [];
        draft.fetchedAt = action.payload?.fetchedAt ?? null;
        break;

      case FETCH_PERMISSIONS_OVERVIEW_FAILURE:
        draft.isFetching = false;
        draft.error = true;
        draft.forbidden = action.payload?.forbidden === true;
        break;

      case FETCH_PERMISSION_HISTORY: {
        const nextUuid = action.payload?.uuid ?? null;
        draft.isFetchingHistory = true;
        // A different person means the entries on hand are the wrong ones — drop them
        // rather than letting a stale list flash under a new name.
        if (nextUuid !== state.historyForUuid) {
          draft.historyEntries = null;
          draft.historyAvailable = true;
        }
        draft.historyForUuid = nextUuid;
        break;
      }

      case FETCH_PERMISSION_HISTORY_SUCCESS:
        draft.isFetchingHistory = false;
        draft.historyForUuid = action.payload?.uuid ?? state.historyForUuid;
        draft.historyEntries = action.payload?.entries ?? [];
        draft.historyAvailable = action.payload?.available !== false;
        break;

      case FETCH_PERMISSION_HISTORY_FAILURE:
        draft.isFetchingHistory = false;
        // An outright failure reads the same as "nothing to show" in the drawer: history
        // is reference material and must never block the editor above it.
        draft.historyEntries = [];
        break;

      default:
        break;
    }
  });

export default EmployeePermissionsReducer;
